import { z } from "zod";

export const lineItemSchema = z.object({
  id: z.string().optional(),
  description: z.string().min(1, { message: "Description is required" }),
  amount: z.coerce.number().positive({ message: "Amount must be positive" }),
});

export type LineItemValues = z.infer<typeof lineItemSchema>;

export const invoiceFormSchema = z
  .object({
    customerId: z.string().min(1, { message: "Please select a customer" }),
    currency: z.string().min(1, { message: "Currency is required" }),
    amount: z.coerce.number().positive({ message: "Amount must be positive" }),
    description: z.string().optional(),
    dueDate: z.date({ required_error: "Due date is required" }),
    paymentMethods: z.array(z.string()).default([]),
    // Enhanced invoice fields (STA-682)
    poNumber: z.string().optional(),
    projectName: z.string().optional(),
    notes: z.string().optional(),
    lineItems: z
      .array(lineItemSchema)
      .default([{ description: "", amount: 0 }]),
    // Recurring invoice fields
    invoiceType: z.enum(["one-time", "recurring"]).default("one-time"),
    frequency: z.coerce.number().int().min(1).default(1),
    frequencyUnit: z.enum(["months"]).default("months"),
    dueDateType: z.enum(["immediate", "net_days"]).default("immediate"),
    dueDateDays: z.coerce.number().int().min(0).max(180).optional(),
    endCondition: z.enum(["never", "after_count"]).default("never"),
    endAfterCount: z.coerce.number().int().min(1).nullable().optional(),
  })
  .refine(
    (data) => {
      // Payment methods required only for one-time invoices
      if (data.invoiceType === "one-time" && data.paymentMethods.length === 0) {
        return false;
      }
      return true;
    },
    {
      message: "Please select at least one payment method",
      path: ["paymentMethods"],
    },
  )
  .refine(
    (data) => {
      // If recurring and dueDateType is net_days, dueDateDays must be at least 1
      if (
        data.invoiceType === "recurring" &&
        data.dueDateType === "net_days" &&
        (data.dueDateDays === undefined ||
          data.dueDateDays === null ||
          data.dueDateDays < 1)
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Number of days must be at least 1",
      path: ["dueDateDays"],
    },
  )
  .refine(
    (data) => {
      // If recurring and endCondition is after_count, endAfterCount is required
      if (
        data.invoiceType === "recurring" &&
        data.endCondition === "after_count" &&
        (data.endAfterCount === undefined || data.endAfterCount === null)
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Number of invoices is required",
      path: ["endAfterCount"],
    },
  );

export type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;
